import React, { useState, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { playbookApi } from '../api/playbookApi';
import type { PlaybookInstance } from '../types/playbook.types';

interface AdminInstanceActionsProps {
  instanceId: string;
  instance: PlaybookInstance;
  onUpdate: () => void;
}

type ConfirmAction = 'reset' | 'cancel' | null;

const AdminInstanceActions: React.FC<AdminInstanceActionsProps> = ({ instanceId, instance, onUpdate }) => {
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [reason, setReason] = useState('');
  const [archiveTask, setArchiveTask] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleReset = useCallback(async () => {
    if (!reason.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await playbookApi.resetInstance(instanceId, { reason: reason.trim() });
      setConfirmAction(null);
      setReason('');
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset failed');
    } finally {
      setLoading(false);
    }
  }, [instanceId, reason, onUpdate]);

  const handleCancel = useCallback(async () => {
    if (!reason.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await playbookApi.cancelInstance(instanceId, { reason: reason.trim(), archiveTask });
      setConfirmAction(null);
      setReason('');
      onUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cancel failed');
    } finally {
      setLoading(false);
    }
  }, [instanceId, reason, archiveTask, onUpdate]);

  const closeConfirm = () => {
    setConfirmAction(null);
    setReason('');
    setArchiveTask(false);
    setError(null);
  };

  return (
    <>
      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
        <button
          type="button"
          onClick={() => setConfirmAction('reset')}
          className="rounded-md border border-amber-300 px-2.5 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900/30"
        >
          Reset Workflow
        </button>
        <button
          type="button"
          onClick={() => setConfirmAction('cancel')}
          disabled={!!instance.completed_at}
          className="rounded-md border border-rose-300 px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-rose-700 dark:text-rose-300 dark:hover:bg-rose-900/30"
        >
          Cancel Workflow
        </button>
      </div>

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeConfirm} />
          <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-sm w-full overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                {confirmAction === 'reset' ? 'Reset Workflow' : 'Cancel Workflow'}
              </h3>
              <button onClick={closeConfirm} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {confirmAction === 'reset'
                  ? 'This will reset the workflow back to its initial status. All progress will be retained in the audit log.'
                  : 'This will terminate the workflow. This action cannot be undone.'
                }
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Reason <span className="text-rose-500">*</span>
                </label>
                <textarea
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-lex-accent"
                  rows={2}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Explain why..."
                />
              </div>
              {confirmAction === 'cancel' && (
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={archiveTask}
                    onChange={(e) => setArchiveTask(e.target.checked)}
                    className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent dark:border-gray-600"
                  />
                  Also archive linked task
                </label>
              )}
              {error && (
                <p className="text-xs text-rose-600 dark:text-rose-400">{error}</p>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={closeConfirm}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmAction === 'reset' ? handleReset : handleCancel}
                disabled={!reason.trim() || loading}
                className={`rounded-md px-3 py-1.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60 ${
                  confirmAction === 'reset'
                    ? 'bg-amber-600 hover:bg-amber-700'
                    : 'bg-rose-600 hover:bg-rose-700'
                }`}
              >
                {loading ? 'Processing...' : confirmAction === 'reset' ? 'Reset' : 'Cancel Workflow'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminInstanceActions;
